import React, { useEffect, useRef, useState } from 'react'

import { usePostPartnersAdpEmbeddedResetEmployerData } from 'utils/web_api'
import { type PartnersAdpResetResponse } from 'utils/web_api.schemas'

interface StatusState {
  type: 'idle' | 'loading' | 'success' | 'error'
  message: string
}

const ADPEmbeddedResetPageImpl: React.FC = () => {
  const [status, setStatus] = useState<StatusState>({ type: 'idle', message: '' })
  const jwtTokenRef = useRef<string | null>(null)
  const resetStartedRef = useRef(false)

  const resetMutation = usePostPartnersAdpEmbeddedResetEmployerData({
    mutation: {
      onSuccess: (data: PartnersAdpResetResponse) => {
        setStatus({
          type: 'success',
          message: data.message
        })
      },
      onError: (error: any) => {
        console.error('Error during reset:', error)
        let errorMessage = 'Reset operation failed. Please try again.'

        // Extract error message from response if available
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error?.message) {
          errorMessage = error.message
        }

        setStatus({
          type: 'error',
          message: errorMessage
        })
      }
    }
  })

  useEffect(() => {
    // Extract token from URL query parameters
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')

    if (!token) {
      setStatus({
        type: 'error',
        message: 'No JWT token provided in URL parameters. Please ensure the URL includes a valid token parameter.'
      })
      return
    }

    jwtTokenRef.current = token

    // Auto-start reset process when token is available
    if (!resetStartedRef.current) {
      resetStartedRef.current = true
      performReset(token)
    }
  }, [])

  const performReset = (token: string) => {
    setStatus({ type: 'loading', message: 'Resetting ADP employer data...' })
    resetMutation.mutate({ data: { token } })
  }

  const handleRetry = () => {
    if (jwtTokenRef.current) {
      resetStartedRef.current = false
      performReset(jwtTokenRef.current)
    }
  }

  if (status.type === 'loading') {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='bg-white shadow-lg rounded-lg p-8 max-w-md w-full mx-4'>
          <div className='text-center'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4'>
              <svg className='animate-spin h-6 w-6 text-blue-600' fill='none' viewBox='0 0 24 24'>
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
              </svg>
            </div>
            <h2 className='text-xl font-semibold text-gray-900 mb-2'>
              Resetting ADP Data
            </h2>
            <p className='text-gray-600'>{status.message}</p>
            <p className='text-sm text-gray-500 mt-2'>
              This may take a few moments...
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50'>
      <div className='bg-white shadow-lg rounded-lg p-8 max-w-md w-full mx-4'>
        <div className='text-center'>
          {status.type === 'success'
            ? (
              <>
                <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4'>
                  <svg className='h-6 w-6 text-green-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                  </svg>
                </div>
                <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                  Reset Successful
                </h2>
                <p className='text-gray-600 mb-4'>{status.message}</p>

                <p className='text-sm text-gray-500'>
                  You can now re-test the ADP onboarding flow with the same credentials.
                </p>
              </>
              )
            : (
              <>
                <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4'>
                  <svg className='h-6 w-6 text-red-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </div>
                <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                  Reset Failed
                </h2>
                <p className='text-gray-600 mb-4'>{status.message}</p>

                <button
                  onClick={handleRetry}
                  className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                >
                  Try Again
                </button>
              </>
              )}
        </div>
      </div>
    </div>
  )
}

const ADPEmbeddedResetPage: React.FC = () => {
  return (
    <ADPEmbeddedResetPageImpl />
  )
}

export default ADPEmbeddedResetPage
